import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { ProductForm } from '@/components/forms/ProductForm';
import { getProduct, updateProduct } from '@/api/products';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore';
import type { UpdateProductRequest } from '@/types/product';

/**
 * 编辑产品页面
 */
export const ProductEdit = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const developerId = getCurrentDeveloperId();

  // 查询产品详情
  const productQuery = useQuery({
    queryKey: ['product', developerId, productId],
    queryFn: () => {
      if (!productId) {
        throw new Error('产品 ID 不能为空');
      }
      return getProduct(productId);
    },
    enabled: !!productId && !!developerId,
  });

  const product = productQuery.data;
  const loading = productQuery.isLoading;
  const error = productQuery.error;

  /**
   * 处理产品更新
   */
  const handleUpdate = async (values: UpdateProductRequest): Promise<void> => {
    if (!productId || !product) return;

    await updateProduct(productId, values);
    message.success(`产品 "${product.name}" 更新成功`);
    
    // ✅ 更新产品后，清除相关的缓存
    await Promise.allSettled([
      // 清除当前产品的详情缓存
      queryClient.invalidateQueries({ queryKey: ['product', developerId, productId] }),
      // 清除产品列表缓存
      queryClient.invalidateQueries({ queryKey: ['products', developerId] }),
      // 清除所有产品缓存（用于 App SDK 编辑页面的绑定选择）
      queryClient.invalidateQueries({ queryKey: ['all-products', developerId] }),
      // 清除相关的产品查询缓存
      queryClient.invalidateQueries({ queryKey: ['products'] }),
      // 如果有分页相关的缓存也需要清除
      queryClient.invalidateQueries({ queryKey: ['products', developerId, 1] }),
      queryClient.invalidateQueries({ queryKey: ['products', developerId, 2] }),
      queryClient.invalidateQueries({ queryKey: ['products', developerId, 3] }),
      // 可以根据实际情况调整页数范围
    ]);
    
    navigate(`/products/${productId}`);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorAlert 
        message="加载失败" 
        description="无法加载产品信息，请检查网络或稍后重试。"
        className="mb-8"
      />
    );
  }

  if (!product) {
    return (
      <ErrorAlert 
        message="未找到产品" 
        description="指定的产品不存在或已被删除"
        className="mb-8"
      />
    );
  }

  // 构建表单初始值
  const initialValues = {
    name: product.name,
    category_id: product.category_id,
    ios_panel_id: product.ios_panel_id || undefined,
    android_panel_id: product.android_panel_id || undefined,
  };

  return (
    <div className="w-full space-y-8">
      {/* 统一页面标题层级 */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">编辑产品</h1>
        <p className="text-gray-500 mt-1">修改产品名称及关联面板配置，修改后已集成的App将同步生效。</p>
      </div>
      <div className="max-w-2xl w-full">
        <ProductForm 
          mode="edit" 
          initialValues={initialValues}
          onSubmit={(values) => handleUpdate(values as UpdateProductRequest)} 
        />
      </div>
    </div>
  );
};